import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { staffLogOut } from "../store/redux/universeStaffReducer.js";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import MapRoundedIcon from "@mui/icons-material/MapRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CEOThemeSwitcher from "./CEOThemeSwitcher";
import CEOOverview from "./CEOOverview";
import CEOOrganization from "./CEOOrganization";
import CEOTerritories from "./CEOTerritories";
import CEOOnboarding from "./CEOOnboarding";
import CEOSubscriptions from "./CEOSubscriptions";
import CEOApprovals from "./CEOApprovals";
import CEOOperations from "./CEOOperations";
import { CEOThemeProvider } from "./CEOThemeProvider";
import "./ceoShell.css";
import "./CEOIntelligenceModal.css";

const CEOGeoWorkspace = lazy(() => import("../geo-intelligence/ceo/CEOGeoWorkspace.jsx"));

class IntelligenceErrorBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    if (this.state.failed) return <div className="ceo-intelligence-loading" role="alert">
      <p>Universe Intelligence could not load. Close this window and try again.</p>
      <button type="button" onClick={this.props.onClose}>Return to dashboard</button>
    </div>;
    return this.props.children;
  }
}

function CEOIntelligenceModal({ onClose, returnFocusTo }) {
  const dialogRef = useRef(null);
  useEffect(() => {
    const previousBody = document.body.style.overflow;
    const previousHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousHtml;
      window.requestAnimationFrame(() => returnFocusTo?.isConnected && returnFocusTo.focus());
    };
  }, [returnFocusTo]);
  const onKeyDown = (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
      event.preventDefault(); event.stopPropagation();
      const input = dialogRef.current?.querySelector("#ugi-global-input");
      if (input) input.focus();
      else dialogRef.current?.querySelector('[aria-label="Search records"]')?.click();
      return;
    }
    if (event.key === "Escape") {
      if (dialogRef.current?.querySelector('.ugi-detail[role="dialog"], [data-ceo-panel-open="true"]')) return;
      event.stopPropagation(); onClose(); return;
    }
    if (event.key !== "Tab" || dialogRef.current?.querySelector('.ugi-detail[role="dialog"], [data-ceo-panel-open="true"]')) return;
    const focusable = [...(dialogRef.current?.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') || [])]
      .filter(el => el.getClientRects().length && !el.closest('[aria-hidden="true"]'));
    if (!focusable.length) { event.preventDefault(); dialogRef.current?.focus(); return; }
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  return createPortal(<div className="ceo-intelligence-modal" role="dialog" aria-modal="true" aria-label="Universe Intelligence" tabIndex={-1} ref={dialogRef} onKeyDown={onKeyDown}>
    <IntelligenceErrorBoundary onClose={onClose}>
      <Suspense fallback={<div className="ceo-intelligence-loading" role="status">Opening Universe Intelligence…</div>}>
        <CEOGeoWorkspace onClose={onClose} closeVariant="power" initialView="world" focusCloseOnMount />
      </Suspense>
    </IntelligenceErrorBoundary>
  </div>, document.body);
}


export const CEO_SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    group: "Command",
    icon: DashboardRoundedIcon,
  },
  {
    id: "organization",
    label: "Organization",
    group: "Command",
    icon: GroupsRoundedIcon,
  },
  {
    id: "territories",
    label: "Territories",
    group: "Command",
    icon: PublicRoundedIcon,
  },
  {
    id: "onboarding",
    label: "Onboarding",
    group: "Operations",
    icon: AddRoundedIcon,
  },
  {
    id: "clients",
    label: "Clients",
    group: "Operations",
    icon: BusinessRoundedIcon,
  },
  {
    id: "subscriptions",
    label: "Subscriptions",
    group: "Operations",
    icon: AccountBalanceRoundedIcon,
  },
  {
    id: "finance",
    label: "Finance",
    group: "Operations",
    icon: AccountBalanceRoundedIcon,
  },
  {
    id: "approvals",
    label: "Approvals",
    group: "Operations",
    icon: TaskAltRoundedIcon,
  },
  {
    id: "operations",
    label: "Operations",
    group: "Operations",
    icon: MonitorHeartRoundedIcon,
  },
  {
    id: "map",
    label: "Universe Intelligence",
    group: "Insights",
    icon: MapRoundedIcon,
  },
  {
    id: "reports",
    label: "Reports",
    group: "Insights",
    icon: AssessmentRoundedIcon,
  },
  {
    id: "governance",
    label: "Governance",
    group: "Insights",
    icon: AdminPanelSettingsRoundedIcon,
  },
  {
    id: "settings",
    label: "Appearance",
    group: "Insights",
    icon: SettingsRoundedIcon,
  },
];

const NAV_KEY = "universe:ceo-nav-state";
const NAV_STATES = ["expanded", "collapsed", "hidden"];

function savedNavState() {
  try {
    const saved = window.localStorage.getItem(NAV_KEY);
    return NAV_STATES.includes(saved) ? saved : "expanded";
  } catch {
    return "expanded";
  }
}

function DefaultPage({
  operations,
  approvals,
  subscriptions,
  onboarding,
  section,
  navigateTo,
  loadOverview,
  loadManagement,
  onAssignTerritory,
  onTransferStaff,
  onSetTarget,
  onOnboardClient,
  onOnboardStaff,
}) {
  if (section.id === "operations")
    return <CEOOperations {...operations} navigateTo={navigateTo} />;
  if (section.id === "approvals") return <CEOApprovals {...approvals} />;
  if (section.id === "subscriptions")
    return <CEOSubscriptions {...subscriptions} />;
  if (section.id === "onboarding") return <CEOOnboarding {...onboarding} />;
  if (section.id === "overview") {
    return (
      <CEOOverview
        navigateTo={navigateTo}
        loadOverview={loadOverview}
        onOnboardClient={onOnboardClient}
        onOnboardStaff={onOnboardStaff}
      />
    );
  }
  if (section.id === "organization") {
    return (
      <CEOOrganization
        loadManagement={loadManagement}
        onTransferStaff={onTransferStaff}
        onOnboardStaff={onOnboardStaff}
      />
    );
  }
  if (section.id === "territories") {
    return (
      <CEOTerritories
        loadManagement={loadManagement}
        onAssignTerritory={onAssignTerritory}
        onSetTarget={onSetTarget}
      />
    );
  }
  if (section.id === "settings") {
    return (
      <section className="ceo-surface ceo-shell__settings">
        <CEOThemeSwitcher />
      </section>
    );
  }
  return (
    <section className="ceo-surface ceo-shell__empty">
      <span className="ceo-shell__empty-icon">
        <section.icon fontSize="inherit" />
      </span>
      <h2>{section.label}</h2>
      <p className="ceo-subtle">
        The {section.label.toLowerCase()} screen connects to company data in the
        following implementation steps. This workspace shell is ready for that
        screen.
      </p>
    </section>
  );
}

/** Mount beneath a React Router route. renderSection receives {section, navigateTo}. */
function CEOWorkspaceInner({
  executiveName = "Chief Executive Officer",
  renderSection,
  loadOverview,
  loadManagement,
  onAssignTerritory,
  onTransferStaff,
  onSetTarget,
  onOnboardClient: customOnboardClient,
  onOnboardStaff: customOnboardStaff,
  onboarding = {},
  subscriptions = {},
  approvals = {},
  operations = {},
  onLogout,
  notifications = [],
}) {
  const [params, setParams] = useSearchParams();
  const requestedPage = params.get("page");
  const section =
    CEO_SECTIONS.find((item) => item.id === requestedPage) || CEO_SECTIONS[0];
  const [navState, setNavState] = useState(savedNavState);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const [intelligenceOpen, setIntelligenceOpen] = useState(false);
  const intelligenceOpener = useRef(null);
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);
  const mobileOpenRef = useRef(null);
  const mobileCloseRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(NAV_KEY, navState);
    } catch {
      /* Storage is optional. */
    }
  }, [navState]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    mobileCloseRef.current?.focus();
    return () => {
      document.body.style.overflow = previous;
      mobileOpenRef.current?.focus();
    };
  }, [mobileOpen]);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        setMenu(null);
      }
      if (mobileOpen && event.key === "Tab") {
        const focusable = [
          ...document.querySelectorAll(
            ".ceo-shell__mobile-sidebar button:not([disabled])",
          ),
        ];
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [mobileOpen]);

  useEffect(() => {
    if (!menu) return undefined;
    const closeOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) setMenu(null);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [menu]);

  function navigateTo(page) {
    if (!CEO_SECTIONS.some((item) => item.id === page)) return;
    if (page === "map") {
      intelligenceOpener.current = document.activeElement;
      setIntelligenceOpen(true);
      setMobileOpen(false);
      setMenu(null);
      setSearch("");
      return;
    }
    setIntelligenceOpen(false);
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("page", page);
      next.delete("view");
      next.delete("id");
      return next;
    });
    setMobileOpen(false);
    setMenu(null);
    setSearch("");
  }

  function openOnboarding(kind) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("page", "onboarding");
      next.set("view", kind);
      next.delete("id");
      return next;
    });
    setMobileOpen(false);
    setMenu(null);
  }
  const onOnboardClient =
    customOnboardClient || (() => openOnboarding("client"));
  const onOnboardStaff = customOnboardStaff || (() => openOnboarding("staff"));
  const matches = search.trim()
    ? CEO_SECTIONS.filter((item) =>
        item.label.toLowerCase().includes(search.trim().toLowerCase()),
      )
    : [];
  const unread = notifications.filter((item) => !item.read).length;
  const grouped = [...new Set(CEO_SECTIONS.map((item) => item.group))];

  const sidebar = (
    <>
      <div className="ceo-shell__brand-row">
        <button
          className="ceo-shell__brand"
          aria-label="Go to CEO overview"
          onClick={() => navigateTo("overview")}
          type="button"
        >
          <span className="ceo-shell__brand-symbol">U</span>
          <span className="ceo-shell__brand-words">
            <strong>Universe</strong>
            <small>EXECUTIVE</small>
          </span>
        </button>
        <div className="ceo-shell__header-controls" role="group" aria-label="Sidebar display options">
          <button
            className="ceo-shell__header-control"
            onClick={() => setNavState((state) => state === "collapsed" ? "expanded" : "collapsed")}
            title={navState === "collapsed" ? "Expand navigation" : "Show icons only"}
            aria-label={navState === "collapsed" ? "Expand navigation" : "Show icons only"}
            type="button"
          >
            {navState === "collapsed" ? <ChevronRightRoundedIcon fontSize="large" /> : <ChevronLeftRoundedIcon fontSize="large" />}
          </button>
          <button
            className="ceo-shell__header-control"
            onClick={() => setNavState("hidden")}
            title="Hide navigation"
            aria-label="Hide navigation"
            type="button"
          >
            <VisibilityOffRoundedIcon fontSize="large" />
          </button>
        </div>
        <button
          ref={mobileCloseRef}
          className="ceo-shell__mobile-close"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
          type="button"
        >
          <CloseRoundedIcon fontSize="large" />
        </button>
      </div>
      <section className="ceo-shell__identity-card" aria-label="Executive workspace">
        <span className="ceo-shell__identity-kicker">Executive workspace</span>
        <strong title={executiveName}>{executiveName}</strong>
        <span className="ceo-shell__identity-caption">Universe Command Center</span>
      </section>
      <div className="ceo-shell__sidebar-scroll">
        {grouped.map((group) => (
          <div className="ceo-shell__nav-group" key={group}>
            <div className="ceo-shell__group-heading">
              <span className="ceo-shell__group-label">{group}</span>
              <span className="ceo-shell__group-count" aria-hidden="true">
                {CEO_SECTIONS.filter((item) => item.group === group).length}
              </span>
            </div>
            <nav aria-label={`${group} navigation`}>
              {CEO_SECTIONS.filter((item) => item.group === group).map(
                (item) => (
                  <button
                    className={`ceo-shell__nav-link ${(item.id === "map" ? intelligenceOpen : section.id === item.id) ? "is-active" : ""}`}
                    aria-current={section.id === item.id && !intelligenceOpen ? "page" : undefined}
                    aria-label={item.label}
                    title={navState === "collapsed" ? item.label : undefined}
                    key={item.id}
                    onClick={() => navigateTo(item.id)}
                    type="button"
                  >
                    <item.icon fontSize="large" />
                    <span>{item.label}</span>
                  </button>
                ),
              )}
            </nav>
          </div>
        ))}
      </div>
      <div className="ceo-shell__sidebar-foot">
        <section className="ceo-shell__context-card" aria-label="Current CEO section">
          <span className="ceo-shell__context-kicker">Current section</span>
          <div className="ceo-shell__context-detail">
            <section.icon fontSize="large" aria-hidden="true" />
            <strong>{section.label}</strong>
          </div>
          <span className="ceo-shell__context-caption">{section.group} workspace</span>
        </section>
      </div>
    </>
  );

  return (
    <div className="ceo-shell" data-nav={navState}>
      <aside
        className="ceo-shell__sidebar"
        aria-label="CEO workspace navigation"
      >
        {sidebar}
      </aside>
      {mobileOpen && (
        <div className="ceo-shell__mobile-layer">
          <div
            className="ceo-shell__mobile-backdrop"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="ceo-shell__mobile-sidebar"
            aria-label="Mobile CEO navigation"
            aria-modal="true"
            role="dialog"
          >
            {sidebar}
          </aside>
        </div>
      )}
      <div className="ceo-shell__body">
        <header className="ceo-shell__topbar">
          <div className="ceo-shell__topbar-start">
            <button
              ref={mobileOpenRef}
              className="ceo-shell__icon-button ceo-shell__mobile-trigger"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
              type="button"
            >
              <MenuRoundedIcon />
            </button>
            {navState === "hidden" && (
              <button
                className="ceo-shell__icon-button ceo-shell__restore"
                aria-label="Show navigation"
                title="Show navigation"
                onClick={() => setNavState("expanded")}
                type="button"
              >
                <ChevronRightRoundedIcon />
              </button>
            )}
            <div className="ceo-shell__breadcrumb">
              <span>CEO workspace</span>
              <ChevronRightRoundedIcon fontSize="small" />
              <strong>{section.label}</strong>
            </div>
          </div>
          <div className="ceo-shell__topbar-end" ref={menuRef}>
            <div className="ceo-shell__search">
              <button
                className="ceo-shell__search-trigger"
                aria-label="Focus section search"
                onClick={() => searchRef.current?.focus()}
                type="button"
              >
                <SearchRoundedIcon fontSize="small" />
              </button>
              <input
                ref={searchRef}
                aria-label="Search CEO sections"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search sections"
              />
              <kbd>Ctrl K</kbd>
              {matches.length > 0 && (
                <div
                  className="ceo-shell__search-results"
                  role="listbox"
                  aria-label="Matching sections"
                >
                  {matches.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => navigateTo(item.id)}
                      type="button"
                      role="option"
                      aria-selected={section.id === item.id}
                    >
                      <item.icon fontSize="small" /> {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="ceo-shell__menu-anchor">
              <button
                className="ceo-shell__icon-button"
                aria-label={`Notifications${unread ? `, ${unread} unread` : ""}`}
                aria-expanded={menu === "notifications"}
                onClick={() =>
                  setMenu(menu === "notifications" ? null : "notifications")
                }
                type="button"
              >
                <NotificationsNoneRoundedIcon />
                {unread > 0 && <span className="ceo-shell__notification-dot" />}
              </button>
              {menu === "notifications" && (
                <div
                  className="ceo-shell__popover"
                  role="region"
                  aria-label="Notifications"
                >
                  <strong>Notifications</strong>
                  {notifications.length ? (
                    notifications
                      .slice(0, 5)
                      .map((item, index) => (
                        <p key={item.id || index}>
                          {item.title || item.message}
                        </p>
                      ))
                  ) : (
                    <p className="ceo-subtle">No notifications yet.</p>
                  )}
                </div>
              )}
            </div>
            <div className="ceo-shell__menu-anchor">
              <button
                className="ceo-shell__profile"
                aria-label="Open CEO profile menu"
                aria-expanded={menu === "profile"}
                onClick={() => setMenu(menu === "profile" ? null : "profile")}
                type="button"
              >
                <span className="ceo-shell__avatar">CEO</span>
                <span className="ceo-shell__profile-name">{executiveName}</span>
                <ExpandMoreRoundedIcon fontSize="small" />
              </button>
              {menu === "profile" && (
                <div
                  className="ceo-shell__popover ceo-shell__popover--profile"
                  role="menu"
                >
                  <div className="ceo-shell__profile-details">
                    <strong>{executiveName}</strong>
                    <small>Chief Executive Officer</small>
                  </div>
                  <button
                    role="menuitem"
                    onClick={() => navigateTo("settings")}
                    type="button"
                  >
                    <SettingsRoundedIcon fontSize="small" /> Appearance
                  </button>
                  {onLogout && (
                    <button role="menuitem" onClick={onLogout} type="button">
                      <LogoutRoundedIcon fontSize="small" /> Log out
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>
        <main id="ceo-main" className="ceo-shell__main">
          <div className="ceo-shell__page-head">
            <div>
              <span className="ceo-shell__eyebrow">UNIVERSE / CEO</span>
              <h1>{section.label}</h1>
              <p className="ceo-subtle">Company-wide workspace</p>
            </div>
            {onOnboardClient && (
              <button
                className="ceo-button ceo-button--primary"
                onClick={onOnboardClient}
                type="button"
              >
                <AddRoundedIcon fontSize="small" /> Onboard client
              </button>
            )}
          </div>
          {renderSection ? (
            renderSection({ section, navigateTo })
          ) : (
            <DefaultPage
              operations={operations}
              approvals={approvals}
              subscriptions={subscriptions}
              onboarding={onboarding}
              section={section}
              navigateTo={navigateTo}
              loadOverview={loadOverview}
              loadManagement={loadManagement}
              onAssignTerritory={onAssignTerritory}
              onTransferStaff={onTransferStaff}
              onSetTarget={onSetTarget}
              onOnboardClient={onOnboardClient}
              onOnboardStaff={onOnboardStaff}
            />
          )}
        </main>
      </div>
      {intelligenceOpen && <CEOIntelligenceModal onClose={() => setIntelligenceOpen(false)} returnFocusTo={intelligenceOpener.current} />}
    </div>
  );
}

export default function CEOWorkspace(props) {
  const dispatch = useDispatch();
  return (
    <CEOThemeProvider>
      <CEOWorkspaceInner onLogout={() => dispatch(staffLogOut())} {...props} />
    </CEOThemeProvider>
  );
}
